import { useEffect, useRef, useState } from 'react';
import { ListProps } from 'components/list';
import { useLazyPageQuery, useSummaryQuery } from './index';
import {
  AnalyticsListKind,
  AnalyticsSummary,
  GetAnalyticsRequest,
} from './types';

export type AnalyticsLoadMoreProps = Pick<
  ListProps,
  'hasMore' | 'onShowMore' | 'isLoadingMore' | 'loadMoreError'
> & { totalCount: number };

const fields = {
  sales: 'checkouts',
  refunds: 'refunds',
  exchanges: 'exchanges',
} as const;
type Lane = {
  rows: AnalyticsSummary['checkouts'] | AnalyticsSummary['refunds'];
  cursor: string | null;
  loading: boolean;
  error?: string;
};
type Snapshot = {
  source: AnalyticsSummary;
  scope: string;
  lanes: Partial<Record<AnalyticsListKind, Lane>>;
};
type Pending = { source: AnalyticsSummary; scope: string; abort: () => void };

// Dashboard summaries cover every matching row; only the visible transaction lists are paged.
export function useDashboardAnalytics(
  args: GetAnalyticsRequest,
  options: { skip: boolean }
) {
  const query = useSummaryQuery(args, options);
  const summary = options.skip ? undefined : query.currentData;
  const scope = JSON.stringify([
    args.companyIds,
    args.eventIds,
    args.startDate,
    args.endDate,
  ]);
  const [trigger] = useLazyPageQuery();
  const [snapshot, setSnapshot] = useState<Snapshot>();
  const latest = useRef({ scope, summary });
  latest.current = { scope, summary };
  const pending = useRef<Partial<Record<AnalyticsListKind, Pending>>>({});
  useEffect(
    () => () => {
      for (const kind of Object.keys(fields) as AnalyticsListKind[]) {
        const request = pending.current[kind];
        if (request?.scope === scope && request.source === summary) {
          request.abort();
          delete pending.current[kind];
        }
      }
    },
    [scope, summary]
  );

  const lanes =
    snapshot && snapshot.source === summary && snapshot.scope === scope
      ? snapshot.lanes
      : {};
  const data = summary && { ...summary };
  const loadMore = {} as Record<AnalyticsListKind, AnalyticsLoadMoreProps>;
  for (const kind of Object.keys(fields) as AnalyticsListKind[]) {
    const field = fields[kind];
    const lane = lanes[kind];
    const cursor = lane ? lane.cursor : summary?.pageInfo[kind].nextCursor;
    if (data && lane) {
      // The API binds the cursor to its lane. Keep the complete summary unchanged.
      (data[field] as Lane['rows']) = lane.rows;
    }
    loadMore[kind] = {
      totalCount: summary?.pageInfo[kind].count ?? 0,
      hasMore: !!cursor,
      isLoadingMore: !!lane?.loading,
      loadMoreError: lane?.error,
      onShowMore: async () => {
        if (!summary || !cursor) return;
        const existing = pending.current[kind];
        if (existing?.source === summary && existing.scope === scope) return;
        const request = trigger({ ...args, kind, cursor, limit: 25 });
        const token = { source: summary, scope, abort: () => request.abort() };
        pending.current[kind] = token;
        const current = () =>
          latest.current.scope === scope &&
          latest.current.summary === summary &&
          pending.current[kind] === token;
        const update = (change: (lane: Lane) => Lane) => {
          if (!current()) return;
          setSnapshot((previous) => {
            if (
              latest.current.scope !== scope ||
              latest.current.summary !== summary
            )
              return previous;
            const old =
              previous?.source === summary && previous.scope === scope
                ? previous.lanes
                : {};
            const prior = old[kind] ?? {
              rows: summary[field],
              cursor,
              loading: false,
            };
            return {
              source: summary,
              scope,
              lanes: { ...old, [kind]: change(prior) },
            };
          });
        };
        update((prior) => ({ ...prior, loading: true, error: undefined }));
        try {
          const page = await request.unwrap();
          update((prior) => {
            const ids = new Set(prior.rows.map((row) => row.id));
            const rows = [
              ...prior.rows,
              ...page.rows.filter((row) => !ids.has(row.id)),
            ] as Lane['rows'];
            return { rows, cursor: page.nextCursor, loading: false };
          });
        } catch {
          update((prior) => ({
            ...prior,
            loading: false,
            error: 'Could not load more. Please try again.',
          }));
        } finally {
          // State updates can run after this callback. Let them check scope/source independently.
          if (pending.current[kind] === token) delete pending.current[kind];
        }
      },
    };
  }
  return { ...query, data, currentData: data, loadMore };
}
